import { produce } from "immer"
import { makeFindAndGetBaseMetadata } from "helpers/base"
import type { SelectedInstrument, SelectedRulesInstrument } from "helpers/types/instruments/selected"
import type { Preferences } from "hooks/use-preferences/types"
import { type ActiveInstruments } from "resources/instruments"
import { type UrlInstrument } from "../use-get-instrument-url-params"
import { resolveUrlInstrumentOverride } from "./resolve-url-instrument-override"

const isRulesInstrument = (instrument: SelectedInstrument): instrument is SelectedRulesInstrument => instrument.type === "RULES"

const isComparisonInstrumentInvalid = (instrument: SelectedInstrument, validate: (i: SelectedInstrument) => unknown): boolean => {
  if (isRulesInstrument(instrument) && instrument.rulesConfiguration?.source?.positionSet) {
    return !validate(instrument.rulesConfiguration.source.positionSet)
  }
  return !validate(instrument)
}

export function validateDashboardPreferences(
  preferences: Preferences,
  activeInstruments: ActiveInstruments | null,
  validationOverrides: { urlInstrument?: UrlInstrument } = {},
) {
  if (!activeInstruments) {
    return {
      isCorrected: false,
      preferences,
    }
  }

  const instrumentValidator = makeFindAndGetBaseMetadata(null, activeInstruments)
  let isCorrected = false

  const { urlInstrument } = validationOverrides || {}
  const overrideMainInstrument = resolveUrlInstrumentOverride(
    urlInstrument,
    activeInstruments,
    instrumentValidator,
  )

  const validatedPreferences = produce(preferences, (draft: Preferences = {}) => {
    if (overrideMainInstrument) {
      draft.instrument = overrideMainInstrument
      isCorrected = true
    } else if (draft.instrument && !instrumentValidator(draft.instrument)) {
      delete draft.instrument
      isCorrected = true
    }
    if (draft.secondInstrument && !instrumentValidator(draft.secondInstrument)) {
      delete draft.secondInstrument
      isCorrected = true
    }
    if (draft.comparisonInstrument && isComparisonInstrumentInvalid(draft.comparisonInstrument as SelectedInstrument, instrumentValidator)) {
      delete draft.comparisonInstrument
      delete draft.selectedMode
      isCorrected = true
    }
    if (draft.base && !instrumentValidator(draft.base)) {
      delete draft.base
      isCorrected = true
    }
  })

  return {
    isCorrected,
    preferences: validatedPreferences,
  }
}
